import {
  faBolt,
  faChartLine,
  faClock,
  faShieldHalved,
  faUsers,
  faListCheck,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useEffect, useState } from 'react';

import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import { authApi, METEOR_BASE_URL } from '../lib/api';
import { getDdpClient } from '../lib/ddp';
import { getEnabledSocialProviders, type SocialProvider } from '../lib/socialProviders';
import { useSession } from '../lib/useSession';
import { Button, Input, Select, Text } from '@mieweb/ui';

// ─── Types ────────────────────────────────────────────────────────────────────

/** login / signup / forgot = request reset email / reset-confirm = enter new password via token */
type AuthMode = 'login' | 'signup' | 'forgot' | 'reset-confirm';

interface LoginFormProps {
  initialMode?: AuthMode;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getMode(resetTokenInUrl: boolean): AuthMode {
  if (resetTokenInUrl) return 'reset-confirm';
  if (typeof window === 'undefined') return 'login';
  const params = new URLSearchParams(window.location.search);
  const m = params.get('mode');
  if (m === 'signup') return 'signup';
  if (m === 'forgot') return 'forgot';
  return 'login';
}

function setModeParam(mode: AuthMode) {
  const url = new URL(window.location.href);
  // Remove any reset token when explicitly switching modes
  url.searchParams.delete('token');
  url.searchParams.set('mode', mode);
  window.history.replaceState(null, '', url.toString());
}

// ─── Marketing panel bullets ──────────────────────────────────────────────────

const FEATURES = [
  { icon: faClock, text: 'Real-time clock in/out with team visibility' },
  { icon: faListCheck, text: 'Ticket tracking with individual timers' },
  { icon: faUsers, text: 'Team dashboards with member activity' },
  { icon: faBolt, text: 'Instant real-time sync — no polling, no REST' },
  { icon: faShieldHalved, text: 'Role-based access with admin controls' },
  { icon: faChartLine, text: 'Analytics and reporting across all teams' },
] as const;

export const LoginForm: React.FC<LoginFormProps> = ({ initialMode }) => {
  const session = useSession();
  const showDevSignIn = import.meta.env.MODE !== 'production';

  const resetToken =
    typeof window !== 'undefined'
      ? (new URLSearchParams(window.location.search).get('token') ?? undefined)
      : undefined;
  const invitationToken =
    typeof window !== 'undefined'
      ? (new URLSearchParams(window.location.search).get('invite') ?? undefined)
      : undefined;
  const orgInvitationToken =
    typeof window !== 'undefined'
      ? (new URLSearchParams(window.location.search).get('org_invite') ?? undefined)
      : undefined;
  const joinTeamCode =
    typeof window !== 'undefined'
      ? (new URLSearchParams(window.location.search).get('join') ?? undefined)
      : undefined;

  const [mode, setMode] = useState<AuthMode>(initialMode ?? getMode(!!resetToken));
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [migrationInfo, setMigrationInfo] = useState<string | null>(null);
  const [selectedDomain, setSelectedDomain] = useState<'enterprise' | 'organization'>(
    'organization',
  );
  const [selectedLoginType, setSelectedLoginType] = useState('member');
  const [joinTeam, setJoinTeam] = useState(false);
  const [invitedTeamName, setInvitedTeamName] = useState<string | null>(null);
  const [invitedOrgName, setInvitedOrgName] = useState<string | null>(null);
  const [joinTeamName, setJoinTeamName] = useState<string | null>(null);

  const isSignup = mode === 'signup';
  const isForgot = mode === 'forgot';
  const isResetConfirm = mode === 'reset-confirm';

  useEffect(() => {
    if (!invitationToken) return;
    let active = true;
    const ddp = getDdpClient();
    void ddp
      .getTeamInvitation(invitationToken)
      .then((invitation) => {
        if (!active) return;
        setEmail(invitation.email);
        setInvitedTeamName(invitation.teamName);
      })
      .catch((err: unknown) => {
        if (!active) return;
        setError((err as Error).message || 'This invitation is no longer available.');
      });
    return () => {
      active = false;
    };
  }, [invitationToken]);

  useEffect(() => {
    if (!joinTeamCode) return;
    let active = true;
    const ddp = getDdpClient();
    void ddp
      .getTeamByCode(joinTeamCode)
      .then((team) => {
        if (!active) return;
        setJoinTeamName(team.teamName);
      })
      .catch((err: unknown) => {
        if (!active) return;
        setError((err as Error).message || 'This team join link is no longer available.');
      });
    return () => {
      active = false;
    };
  }, [joinTeamCode]);

  useEffect(() => {
    if (!orgInvitationToken) return;
    let active = true;
    const ddp = getDdpClient();
    void ddp
      .getOrgInvitation(orgInvitationToken)
      .then((invitation) => {
        if (!active) return;
        setEmail(invitation.email);
        setInvitedOrgName(invitation.orgName);
      })
      .catch((err: unknown) => {
        if (!active) return;
        setError((err as Error).message || 'This invitation is no longer available.');
      });
    return () => {
      active = false;
    };
  }, [orgInvitationToken]);

  const acceptInvitation = async (ddp: ReturnType<typeof getDdpClient>) => {
    if (invitationToken) {
      await ddp.acceptTeamInvitation(invitationToken);
    }
    if (orgInvitationToken) {
      await ddp.acceptOrgInvitation(orgInvitationToken);
    }
    if (joinTeamCode) {
      // Join via shared QR/link code. Never block a successful login/signup
      // on this — if the code is stale the user still gets their account.
      try {
        await ddp.joinTeamByQrCode(joinTeamCode);
      } catch (err) {
        console.error('[login] QR team join failed:', err);
      }
    }
    if (!invitationToken && !orgInvitationToken && !joinTeamCode) return;
    const url = new URL(window.location.href);
    url.searchParams.delete('invite');
    url.searchParams.delete('org_invite');
    url.searchParams.delete('join');
    url.searchParams.delete('mode');
    window.history.replaceState(null, '', url.toString());
  };

  const loginOptions =
    selectedDomain === 'enterprise'
      ? [
          { value: 'admin', label: 'Admin' },
          { value: 'owner', label: 'Owner' },
        ]
      : [
          { value: 'member', label: 'Member' },
          { value: 'admin', label: 'Admin' },
          { value: 'owner', label: 'Owner' },
        ];

  const switchMode = (next: AuthMode) => {
    setMode(next);
    setModeParam(next);
    setError(null);
    setSuccessMessage(null);
    setMigrationInfo(null);
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password || loading) return;
    setLoading(true);
    setError(null);
    try {
      const ddp = getDdpClient();
      await ddp.loginWithPassword(email.trim().toLowerCase(), password);
      await acceptInvitation(ddp);
      await session.refetch();
      // Check for blocking after refetch
      if (session.blockMessage) {
        setError('Your account has been suspended. Please contact your administrator.');
        setLoading(false);
        return;
      }
    } catch (err: unknown) {
      const message = (err as Error).message || 'Invalid email or password';

      // Detect Better Auth users that need password reset
      // The error message is a JSON string with token
      try {
        const data = JSON.parse(message);
        if (data.token && data.message) {
          // This is a Better Auth migration response
          const url = new URL(window.location.href);
          url.searchParams.set('token', data.token);
          url.searchParams.delete('mode');
          window.history.replaceState(null, '', url.toString());
          setMode('reset-confirm');
          setPassword('');
          setConfirmPassword('');
          // Show migration info but keep form visible
          setMigrationInfo('Your account needs migration. Please set a new password below.');
          setError(null);
          setLoading(false);
          return;
        }
      } catch {
        // Not JSON, continue with normal error handling
      }

      if (message.includes('suspended') || message.includes('blocked')) {
        setError('Your account has been suspended. Please contact your administrator.');
      } else {
        setError(message);
      }
      setLoading(false);
    }
  };

  const handleDevSignOn = () => {
    if (!showDevSignIn || loading) return;
    setLoading(true);
    setError(null);
    void authApi
      .devMemberSignIn(selectedDomain, selectedLoginType as 'member' | 'admin' | 'owner', joinTeam)
      .then(async () => {
        await session.refetch();
      })
      .catch((err: unknown) => {
        setError((err as Error).message || 'Dev sign-in failed');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return;
    setError(null);
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      const name = `${firstName.trim()} ${lastName.trim()}`.trim();
      const ddp = getDdpClient();
      await ddp.signUpWithPassword(email.trim().toLowerCase(), password, name);
      await acceptInvitation(ddp);
      await session.refetch();
    } catch (err: unknown) {
      setLoading(false);
      const message = (err as Error).message || 'Signup failed';
      if (message.includes('cannot be used to create an account')) {
        setError('This email address is not allowed to create a new account.');
      } else {
        setError(message);
      }
    }
  };

  const handleForgot = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || loading) return;
    setLoading(true);
    setError(null);
    try {
      const ddp = getDdpClient();
      await ddp.ensureConnected();
      await ddp.call('accounts.sendResetPasswordEmail', {
        email: email.trim().toLowerCase(),
      });
      setSuccessMessage('Check your email for a reset link.');
    } catch (err: unknown) {
      setError((err as Error).message || 'Request failed');
    } finally {
      setLoading(false);
    }
  };

  const handleResetConfirm = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resetToken || loading) return;
    setError(null);
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      const ddp = getDdpClient();
      await ddp.ensureConnected();
      await ddp.call('accounts.resetPassword', {
        token: resetToken,
        newPassword: password,
      });
      const url = new URL(window.location.href);
      url.searchParams.delete('token');

      if (session.user) {
        window.history.replaceState(null, '', url.toString());
        window.location.assign(`${window.location.origin}/app/dashboard`);
        return;
      }

      // Clear token from URL and go to login with success message
      url.searchParams.set('mode', 'login');
      window.history.replaceState(null, '', url.toString());
      setMode('login');
      setPassword('');
      setConfirmPassword('');
      setSuccessMessage('Password reset successfully. You can now sign in.');
    } catch (err: unknown) {
      setError((err as Error).message || 'Password reset failed');
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = isResetConfirm
    ? handleResetConfirm
    : isForgot
      ? handleForgot
      : isSignup
        ? handleSignup
        : handleLogin;

  const socialProviders = getEnabledSocialProviders();
  const [socialLoadingId, setSocialLoadingId] = useState<string | null>(null);
  const [socialError, setSocialError] = useState<string | null>(null);

  const handleSocialSignIn = async (provider: SocialProvider) => {
    if (socialLoadingId) return;
    setSocialLoadingId(provider.id);
    setSocialError(null);
    try {
      // Pending team/org join context (from ?join=/?invite=/?org_invite= links)
      // must survive the full redirect-away-and-back OAuth round trip, since
      // sign-in via social providers never runs the password-flow
      // `acceptInvitation()` logic below.
      const pendingJoinParams: Record<string, string> = {};
      if (joinTeamCode) pendingJoinParams.join = joinTeamCode;
      if (invitationToken) pendingJoinParams.invite = invitationToken;
      if (orgInvitationToken) pendingJoinParams.org_invite = orgInvitationToken;

      if (provider.kind === 'meteor-oauth') {
        const oauthParams = new URLSearchParams(pendingJoinParams);
        if (Capacitor.isNativePlatform()) oauthParams.set('native', '1');
        const oauthUrl = `${METEOR_BASE_URL}${provider.meteorPath}?${oauthParams.toString()}`;
        if (Capacitor.isNativePlatform()) {
          // On native, open in an in-app browser with ?native=1 so the backend
          // redirects to the timehuddle://auth deep link instead of the web URL.
          await Browser.open({ url: oauthUrl, presentationStyle: 'popover' });
        } else {
          window.location.href = oauthUrl;
        }
        return;
      }

      // Existing oauth2 and social handling
      const callbackUrl = new URL(`${window.location.origin}/app/dashboard`);
      for (const [key, value] of Object.entries(pendingJoinParams)) {
        callbackUrl.searchParams.set(key, value);
      }
      const callbackURL = callbackUrl.toString();
      const url =
        provider.kind === 'oauth2'
          ? await authApi.signInWithOAuth2(provider.id, callbackURL)
          : await authApi.signInWithSocial(
              provider.id as 'github' | 'google' | 'apple',
              callbackURL,
            );
      window.location.href = url;
    } catch (err: unknown) {
      const message = (err as Error).message || `${provider.label} sign-in failed`;
      if (message.includes('suspended') || message.includes('blocked')) {
        setSocialError('Your account has been suspended. Please contact your administrator.');
      } else {
        setSocialError(message);
      }
      setSocialLoadingId(null);
    }
  };

  // ── Marketing panel (left) ──────────────────────────────────────────────────

  const MarketingPanel = () => (
    <div className="relative hidden flex-col justify-between overflow-hidden bg-gradient-to-br from-primary-700 via-primary-600 to-primary-800 p-10 text-primary-foreground md:flex md:w-1/2 lg:p-14">
      <div className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-white/10" />
      <div className="pointer-events-none absolute -bottom-16 -left-16 h-56 w-56 rounded-full bg-white/10" />

      <div className="relative z-10 space-y-8">
        <div>
          <h1 className="text-3xl font-bold tracking-tight lg:text-4xl">
            {isSignup
              ? 'Start tracking in minutes'
              : isForgot || isResetConfirm
                ? 'Reset your password'
                : 'Welcome back'}
          </h1>
          <p className="mt-3 max-w-sm text-base leading-relaxed text-primary-foreground/90 lg:text-lg">
            {isSignup
              ? 'Create your account and start tracking time with your team — real-time collaboration built in.'
              : isForgot || isResetConfirm
                ? "Enter your email address and we'll send you a link to reset your password."
                : 'Sign in to pick up where you left off. Your data syncs in real-time.'}
          </p>
        </div>

        <ul className="space-y-3">
          {FEATURES.map(({ icon, text }) => (
            <li key={text} className="flex items-center gap-3 text-sm text-primary-foreground/90">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white/15">
                <FontAwesomeIcon icon={icon} className="text-sm" />
              </span>
              {text}
            </li>
          ))}
        </ul>
      </div>

      <div className="relative z-10 mt-8 flex items-center gap-3 border-t border-white/20 pt-6">
        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white/20 text-sm font-bold">
          TH
        </div>
        <div>
          <p className="text-sm font-semibold">TimeHuddle</p>
          <p className="text-xs text-primary-foreground/75">Real-time Team Collaboration</p>
        </div>
      </div>
    </div>
  );

  // ── Layout ────────────────────────────────────────────────────────────────────

  return (
    <div className="flex min-h-screen w-full font-sans text-neutral-800 dark:text-neutral-100">
      <MarketingPanel />

      <div className="relative flex w-full flex-col items-center justify-center px-6 py-12 md:w-1/2 md:px-12 lg:px-20">
        <div className="w-full max-w-sm">
          {/* Mobile-only branding */}
          <div className="mb-8 md:hidden">
            <h1 className="text-2xl font-bold tracking-tight text-neutral-900 dark:text-neutral-50">
              TimeHuddle
            </h1>
            <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
              Team Time Tracking & Collaboration
            </p>
          </div>

          {/* Heading */}
          <div className="mb-6 space-y-1">
            <h2 className="text-2xl font-bold tracking-tight text-neutral-900 dark:text-neutral-50">
              {isSignup
                ? 'Create your account'
                : isForgot
                  ? 'Forgot your password?'
                  : isResetConfirm
                    ? 'Set a new password'
                    : 'Sign in to your account'}
            </h2>
            <p className="text-sm text-neutral-500 dark:text-neutral-400">
              {isSignup
                ? 'Enter your details to get started'
                : isForgot
                  ? "Enter your email and we'll send a reset link"
                  : isResetConfirm
                    ? 'Enter and confirm your new password'
                    : 'Enter your email and password'}
            </p>
          </div>

          {/* Form */}
          <form onSubmit={onSubmit} noValidate className="space-y-4" aria-live="polite">
            {/* Migration info message (shows above form, doesn't hide fields) */}
            {migrationInfo && (
              <div
                className="rounded-md border border-blue-200 bg-blue-50/60 p-3 text-sm dark:border-blue-700 dark:bg-blue-900/30"
                role="status"
              >
                <p className="leading-relaxed text-blue-800 dark:text-blue-200">{migrationInfo}</p>
              </div>
            )}

            {successMessage ? (
              <div className="space-y-4" role="status">
                <div className="rounded-md border border-green-200 bg-green-50/60 p-3 text-sm dark:border-green-700 dark:bg-green-900/30">
                  <p className="leading-relaxed text-green-800 dark:text-green-200">
                    {successMessage}
                  </p>
                </div>
                <Button
                  variant="primary"
                  fullWidth
                  type="button"
                  onClick={() => switchMode('login')}
                >
                  Go to Sign In
                </Button>
              </div>
            ) : (
              <>
                {(invitedTeamName || invitedOrgName) && (
                  <Text variant="muted" size="sm" as="div" role="status">
                    You were invited to join {invitedTeamName ?? invitedOrgName}. Sign in or create
                    an account with the invited email address to join.
                  </Text>
                )}
                {joinTeamName && (
                  <Text variant="muted" size="sm" as="div" role="status">
                    You&apos;re joining the team {joinTeamName}. Sign in or create an account and
                    you&apos;ll be added automatically.
                  </Text>
                )}

                {/* Name fields (signup only) */}
                {isSignup && (
                  <div className="flex items-start gap-3">
                    <Input
                      label="First name"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      required
                      autoComplete="given-name"
                      placeholder="Jane"
                      disabled={loading}
                    />
                    <Input
                      label="Last name"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      required
                      autoComplete="family-name"
                      placeholder="Doe"
                      disabled={loading}
                    />
                  </div>
                )}

                {/* Email (login / signup / forgot) */}
                {!isResetConfirm && (
                  <Input
                    label="Email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    type="email"
                    required
                    autoComplete="email"
                    inputMode="email"
                    spellCheck={false}
                    placeholder="you@example.com"
                    disabled={loading || !!invitationToken || !!orgInvitationToken}
                  />
                )}

                {/* Password (login / signup / reset-confirm) */}
                {!isForgot && (
                  <Input
                    label={isResetConfirm ? 'New password' : 'Password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    type="password"
                    required
                    autoComplete={isSignup || isResetConfirm ? 'new-password' : 'current-password'}
                    placeholder="••••••••"
                    disabled={loading}
                  />
                )}

                {/* Confirm password (signup + reset-confirm) */}
                {(isSignup || isResetConfirm) && (
                  <Input
                    label="Confirm password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    type="password"
                    required
                    autoComplete="new-password"
                    placeholder="••••••••"
                    disabled={loading}
                  />
                )}

                {error && (
                  <Text variant="destructive" size="xs" weight="medium" as="div" role="alert">
                    {error}
                  </Text>
                )}

                <Button
                  variant="primary"
                  fullWidth
                  type="submit"
                  disabled={loading}
                  isLoading={loading}
                  loadingText="Please wait…"
                >
                  {isSignup
                    ? 'Create account'
                    : isForgot
                      ? 'Send reset link'
                      : isResetConfirm
                        ? 'Set new password'
                        : 'Sign in'}
                </Button>
              </>
            )}
          </form>

          {/* Forgot password (login mode only) */}
          {mode === 'login' && (
            <div className="mt-3 text-center">
              <Button variant="link" size="sm" type="button" onClick={() => switchMode('forgot')}>
                Forgot your password?
              </Button>
            </div>
          )}

          {/* Mode toggle (login ↔ signup only) */}
          {(mode === 'login' || mode === 'signup') && (
            <>
              {/* Divider */}
              {socialProviders.length > 0 && (
                <div className="my-6 flex items-center gap-3">
                  <div className="h-px flex-1 bg-neutral-200 dark:bg-neutral-700" />
                  <span className="text-xs text-neutral-400 dark:text-neutral-500">or</span>
                  <div className="h-px flex-1 bg-neutral-200 dark:bg-neutral-700" />
                </div>
              )}

              {/* Social sign-in providers */}
              {socialProviders.length > 0 && (
                <div className="space-y-3">
                  {socialProviders.map((provider) => {
                    const loading = socialLoadingId === provider.id;
                    return (
                      <Button
                        key={provider.id}
                        variant="outline"
                        fullWidth
                        type="button"
                        onClick={() => handleSocialSignIn(provider)}
                        disabled={socialLoadingId !== null}
                        isLoading={loading}
                        loadingText="Redirecting…"
                        aria-label={`Continue with ${provider.label}`}
                      >
                        {!loading && (
                          <span className="flex items-center justify-center gap-2">
                            <FontAwesomeIcon icon={provider.icon} className="text-base" />
                            Continue with {provider.label}
                          </span>
                        )}
                      </Button>
                    );
                  })}

                  {socialError && (
                    <Text variant="destructive" size="xs" weight="medium" as="div" role="alert">
                      {socialError}
                    </Text>
                  )}
                </div>
              )}

              {/* Divider between social and mode toggle text */}
              <div className="my-4" />

              <p className="text-center text-sm text-neutral-600 dark:text-neutral-400">
                {isSignup ? (
                  <>
                    Already have an account?{' '}
                    <Button
                      variant="link"
                      size="sm"
                      type="button"
                      onClick={() => switchMode('login')}
                    >
                      Sign in
                    </Button>
                  </>
                ) : (
                  <>
                    Don&apos;t have an account?{' '}
                    <Button
                      variant="link"
                      size="sm"
                      type="button"
                      onClick={() => switchMode('signup')}
                    >
                      Sign up
                    </Button>
                  </>
                )}
              </p>
            </>
          )}

          {mode === 'login' && showDevSignIn && (
            <div className="mt-5 rounded-xl border border-dashed border-neutral-300 bg-neutral-50 p-4 dark:border-neutral-700 dark:bg-neutral-900/40">
              <div className="flex flex-col gap-3">
                <div className="w-full sm:max-w-xs">
                  <Select
                    label="Domain"
                    value={selectedDomain}
                    onValueChange={(value) => {
                      const nextDomain = value as 'enterprise' | 'organization';
                      setSelectedDomain(nextDomain);
                      setSelectedLoginType(nextDomain === 'enterprise' ? 'admin' : 'member');
                    }}
                    options={[
                      { value: 'enterprise', label: 'Enterprise' },
                      { value: 'organization', label: 'Organization' },
                    ]}
                  />
                </div>
                <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
                  <div className="w-full sm:max-w-xs">
                    <Select
                      label="Login Type"
                      value={selectedLoginType}
                      onValueChange={(value) => {
                        const nextType = value as 'member' | 'admin' | 'owner';
                        setSelectedLoginType(nextType);
                        if (nextType !== 'member') setJoinTeam(false);
                      }}
                      options={loginOptions}
                    />
                  </div>
                  <Button variant="secondary" type="button" onClick={handleDevSignOn}>
                    Authorize
                  </Button>
                </div>
                {selectedLoginType === 'member' && (
                  <label className="flex items-center gap-2 text-sm text-neutral-600 dark:text-neutral-400">
                    <input
                      type="checkbox"
                      checked={joinTeam}
                      onChange={(e) => setJoinTeam(e.target.checked)}
                      className="h-4 w-4 rounded border-neutral-300 text-blue-600 focus:ring-blue-500"
                    />
                    Join a team
                  </label>
                )}
              </div>
            </div>
          )}

          {/* Back to sign in (forgot / reset-confirm) */}
          {(isForgot || isResetConfirm) && !successMessage && (
            <div className="mt-4 text-center">
              <Button variant="link" size="sm" type="button" onClick={() => switchMode('login')}>
                Back to sign in
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
